import * as i0 from '@angular/core';
import { Injectable, PLATFORM_ID, Inject, Directive, Input, NgModule } from '@angular/core';
import * as i2 from '@angular/router';
import { NavigationEnd } from '@angular/router';
import { filter, tap } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';

class GtagConstantService {
    constructor() { }
    // Commands
    static { this.CONFIG_COMMAND = 'config'; }
    static { this.GET_COMMAND = 'get'; }
    static { this.SET_COMMAND = 'set'; }
    static { this.EVENT_COMMAND = 'event'; }
    static { this.CONSENT_COMMAND = 'consent'; }
    // Events
    static { this.EXCEPTION_EVENT = 'exception'; }
    static { this.TIMING_COMPLETE_EVENT = 'timing_complete'; }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.8", ngImport: i0, type: GtagConstantService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.8", ngImport: i0, type: GtagConstantService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.8", ngImport: i0, type: GtagConstantService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [] });

class GtagCommandService {
    constructor() { }
    /**
     * Allows you to add additional configuration information to targets. This is typically
     * product-specific configuration for a product such as Google Ads or Google Analytics.
     * https://developers.google.com/tag-platform/gtagjs/reference#config
     *
     * @param targetId is an identifier that uniquely identifies the target for hits, such as a Google
     * Analytics property or a Google Ads account
     * @param additionalConfigInfo is one or more parameter-value pairs.
     */
    config(targetId, additionalConfigInfo = {}) {
        gtag(GtagConstantService.CONFIG_COMMAND, targetId, additionalConfigInfo);
    }
    /**
     * Allows you to get various values from gtag.js including values set with the set command.
     * https://developers.google.com/tag-platform/gtagjs/reference#get
     *
     * @param target The target to fetch values from.
     * @param fieldName The name of the field to get.
     * @param callBack A function that will be invoked with the requested field, or undefined if it is unset.
     * @returns
     */
    get(target, fieldName, callBack) {
        return gtag(GtagConstantService.GET_COMMAND, target, fieldName, callBack);
    }
    /**
     * Allows you to set values that persist across all the subsequent gtag() calls on the page.
     * https://developers.google.com/tag-platform/gtagjs/reference#set
     *
     * @param params is a key name and the value that is to persist across gtag() calls.
     */
    set(params = {}) {
        gtag(GtagConstantService.SET_COMMAND, params);
    }
    /**
     * Use the event command to send event data.
     * https://developers.google.com/tag-platform/gtagjs/reference#event
     *
     * @param eventName is either a recommended event or a custom event
     * @param eventParams is one or more parameter-value pairs. Each pair separated by a comma.
     */
    event(eventName, eventParams) {
        gtag(GtagConstantService.EVENT_COMMAND, eventName, eventParams);
    }
    /**
     * Use the consent command to configure consent.
     * https://developers.google.com/tag-platform/gtagjs/reference#consent
     *
     * @param consentArgument one of 'default' or 'update'.
     * @param consentParams
     */
    consent(consentArgument, consentParams) {
        gtag(GtagConstantService.CONSENT_COMMAND, consentArgument, consentParams);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.8", ngImport: i0, type: GtagCommandService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.8", ngImport: i0, type: GtagCommandService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.8", ngImport: i0, type: GtagCommandService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [] });

class GtagAPIService {
    constructor(config, _gtagCmd, _router, platformId) {
        this._gtagCmd = _gtagCmd;
        this._router = _router;
        this._isBrowser = isPlatformBrowser(platformId);
        this._globalGtagConfig = { trackPageviews: true, ...config };
        if (this._globalGtagConfig.trackPageviews && this._isBrowser) {
            _router.events
                .pipe(filter((event) => event instanceof NavigationEnd), tap((event) => {
                this.sendPageviewData();
            }))
                .subscribe();
        }
    }
    debug(...msg) {
        if (this._globalGtagConfig?.debug) {
            console.log('angular-gtag:', ...msg);
        }
    }
    sendConfigurationData(params) {
        try {
            this._gtagCmd.config(this._globalGtagConfig?.trackingId, params);
        }
        catch (err) {
            console.error('Google Analytics config error', err);
        }
    }
    /**
     * Use to send a pageview to Google Analytics
     *
     * https://developers.google.com/analytics/devguides/collection/gtagjs/pages
     *
     * @param params
     */
    sendPageviewData(params) {
        try {
            const defaults = {
                page_path: this._router.url,
                page_title: 'Angular App',
                page_location: window.location.href,
            };
            params = { ...defaults, ...params };
            this._gtagCmd.config(this._globalGtagConfig?.trackingId, params);
            this.debug('pageview', this._globalGtagConfig?.trackingId, params);
        }
        catch (err) {
            console.error('Google Analytics pageview error', err);
        }
    }
    /**
     * Use to send Google Analytics Events.
     *
     * https://developers.google.com/analytics/devguides/collection/gtagjs/events
     * @param event
     */
    sendEventData(event) {
        try {
            let { action, ...params } = event;
            this._gtagCmd.event(action, params);
            this.debug('event', this._globalGtagConfig?.trackingId, action, params);
        }
        catch (err) {
            console.error('Google Analytics event error', err);
        }
    }
    /**
     * Use to send Phone Analytics to measure how users interact with phone numbers on your website.
     *
     * https://developers.google.com/analytics/devguides/collection/gtagjs/phone-analytics
     *
     * @param params
     */
    sendPhoneAnalyticsData(params) {
        try {
            this._gtagCmd.set(params);
        }
        catch (err) {
            console.error('Google Analytics phone analytics error', err);
        }
        this.sendExceptionData();
    }
    /**
     * Use to send a user timing event to Google Analytics
     *
     * https://developers.google.com/analytics/devguides/collection/gtagjs/user-timings
     *
     * @param usertiming
     */
    sendUserTimingData(usertiming) {
        try {
            this._gtagCmd.event(GtagConstantService.TIMING_COMPLETE_EVENT, usertiming);
        }
        catch (err) {
            console.error('Google Analytics usertiming error', err);
        }
    }
    /**
     * Use to send an exception event to Google Analytics
     *
     * https://developers.google.com/analytics/devguides/collection/gtagjs/exceptions
     *
     * @param exception
     */
    sendExceptionData(exception = {}) {
        try {
            this._gtagCmd.event(GtagConstantService.EXCEPTION_EVENT, exception);
        }
        catch (err) {
            console.error('Google Analytics exception error', err);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.8", ngImport: i0, type: GtagAPIService, deps: [{ token: 'config' }, { token: GtagCommandService }, { token: i2.Router }, { token: PLATFORM_ID }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.8", ngImport: i0, type: GtagAPIService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.8", ngImport: i0, type: GtagAPIService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: ['config']
                }] }, { type: GtagCommandService }, { type: i2.Router }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }] });

class GtagEventDirective {
    constructor(_gtag, _renderer, _elemRef) {
        this._gtag = _gtag;
        this._renderer = _renderer;
        this._elemRef = _elemRef;
        this.trackOn = '';
        this.action = '';
        this.category = '';
    }
    ngAfterViewInit() {
        try {
            this._renderer.listen(this._elemRef.nativeElement, this.trackOn, () => {
                this._gtag.sendEventData({
                    action: this.action || this.trackOn,
                    event_category: this.category,
                    ...this.params,
                });
            });
        }
        catch (err) {
            console.error(err);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.8", ngImport: i0, type: GtagEventDirective, deps: [{ token: GtagAPIService }, { token: i0.Renderer2 }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.0.8", type: GtagEventDirective, selector: "[gtagEvent]", inputs: { trackOn: "trackOn", action: "action", category: "category", params: "params" }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.8", ngImport: i0, type: GtagEventDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[gtagEvent]',
                }]
        }], ctorParameters: () => [{ type: GtagAPIService }, { type: i0.Renderer2 }, { type: i0.ElementRef }], propDecorators: { trackOn: [{
                type: Input
            }], action: [{
                type: Input
            }], category: [{
                type: Input
            }], params: [{
                type: Input
            }] } });

class NgxGoogleTagsManagerModule {
    static forRoot(config) {
        return {
            ngModule: NgxGoogleTagsManagerModule,
            providers: [
                GtagAPIService,
                { provide: 'config', useValue: config },
            ],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.8", ngImport: i0, type: NgxGoogleTagsManagerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.8", ngImport: i0, type: NgxGoogleTagsManagerModule, declarations: [GtagEventDirective], exports: [GtagEventDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.8", ngImport: i0, type: NgxGoogleTagsManagerModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.8", ngImport: i0, type: NgxGoogleTagsManagerModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [GtagEventDirective],
                    exports: [GtagEventDirective],
                }]
        }] });

/*
 * Public API Surface of ngx-google-tags-manager
 */

/**
 * Generated bundle index. Do not edit.
 */

export { GtagAPIService, GtagEventDirective, NgxGoogleTagsManagerModule };
